import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import { ArrowLeft, RefreshCw, Save, UserPlus } from 'lucide-react';

const RISK_LEVELS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const ROLE_OPTIONS = [
  // Case-role primary
  'SUBJECT', 'DEFENDANT', 'PLAINTIFF', 'SUSPECT', 'PERSON_OF_INTEREST',
  'FUGITIVE', 'SKIP', 'BAIL_JUMPER', 'MISSING_PERSON', 'VICTIM',
  'COMPLAINANT', 'INFORMANT', 'CONFIDENTIAL_INFORMANT',
  // Legal / court
  'CO_SIGNER', 'INDEMNITOR', 'GUARANTOR', 'BONDSMAN_CONTACT',
  'ATTORNEY', 'PROSECUTOR', 'PUBLIC_DEFENDER', 'JUDGE', 'MAGISTRATE',
  'COURT_CLERK', 'BAILIFF', 'PROBATION_OFFICER', 'PAROLE_OFFICER',
  // Witnesses / references
  'WITNESS', 'EYEWITNESS', 'CHARACTER_WITNESS', 'EXPERT_WITNESS',
  'REFERENCE', 'PERSONAL_REFERENCE', 'PROFESSIONAL_REFERENCE',
  // Family relationships
  'SPOUSE', 'EX_SPOUSE', 'PARTNER', 'EX_PARTNER',
  'MOTHER', 'FATHER', 'STEPFATHER', 'STEPMOTHER',
  'SON', 'DAUGHTER', 'STEPSON', 'STEPDAUGHTER',
  'BROTHER', 'SISTER', 'HALF_BROTHER', 'HALF_SISTER', 'COUSIN',
  'GRANDMOTHER', 'GRANDFATHER', 'GRANDSON', 'GRANDDAUGHTER',
  'AUNT', 'UNCLE', 'NEPHEW', 'NIECE', 'GODPARENT', 'GODCHILD',
  'GUARDIAN', 'WARD', 'IN_LAW', 'FAMILY_MEMBER', 'FAMILY_FRIEND',
  // Professional / associates
  'EMPLOYER', 'EMPLOYEE', 'CO_WORKER', 'SUPERVISOR', 'BUSINESS_PARTNER',
  'LANDLORD', 'TENANT', 'NEIGHBOR', 'ROOMMATE', 'ASSOCIATE', 'ACCOMPLICE',
  // Law-enforcement / investigator
  'INVESTIGATOR', 'LEAD_INVESTIGATOR', 'ASSISTING_INVESTIGATOR',
  'FIELD_AGENT', 'CASE_MANAGER', 'CASE_OFFICER', 'DETECTIVE',
  'RECOVERY_AGENT', 'FUGITIVE_RECOVERY_AGENT',
  // Vehicle-related
  'VEHICLE_OWNER', 'REGISTERED_OWNER', 'LIENHOLDER', 'DRIVER', 'PASSENGER',
  // Administrative / other
  'CLIENT_CONTACT', 'CLIENT_REPRESENTATIVE', 'EMERGENCY_CONTACT',
  'THIRD_PARTY', 'UNKNOWN', 'OTHER', 'CUSTOM',
];

/**
 * Phase B — Routed Subject Add/Edit page. Routes:
 *   /cases/:caseId/subjects/new
 *   /cases/:caseId/subjects/:subjectId/edit
 */
const SubjectFormPage = () => {
  const { caseId, subjectId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('nexus_token');
  const isEdit = Boolean(subjectId);

  const empty = {
    full_name: '', aliases: '', date_of_birth: '', gender: '', race_ethnicity: '',
    physical_description: '', height: '', weight: '', hair_color: '', eye_color: '',
    scars_tattoos_marks: '', employer: '', phone_numbers: '', email_addresses: '',
    social_media: '', criminal_history: '', surveillance_risk_level: 'MEDIUM',
    last_known_address: '', caution_indicators: '', notes: '', role_in_case: 'SUBJECT',
  };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/subjects/${subjectId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const s = await res.json();
      setForm({
        ...empty,
        ...s,
        aliases: (s.aliases || []).join(', '),
        phone_numbers: (s.phone_numbers || []).join(', '),
        email_addresses: (s.email_addresses || []).join(', '),
        social_media: (s.social_media || []).join(', '),
      });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [subjectId, isEdit, token]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.full_name) {
      toast({ title: 'Error', description: 'Full name is required', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        aliases: form.aliases ? form.aliases.split(',').map((s) => s.trim()).filter(Boolean) : [],
        phone_numbers: form.phone_numbers ? form.phone_numbers.split(',').map((s) => s.trim()).filter(Boolean) : [],
        email_addresses: form.email_addresses ? form.email_addresses.split(',').map((s) => s.trim()).filter(Boolean) : [],
        social_media: form.social_media ? form.social_media.split(',').map((s) => s.trim()).filter(Boolean) : [],
      };
      const url = isEdit ? `${BACKEND_URL}/api/subjects/${subjectId}` : `${BACKEND_URL}/api/cases/${caseId}/subjects`;
      const method = isEdit ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      toast({ title: 'Success', description: isEdit ? 'Subject updated' : 'Subject added' });
      navigate(`/cases/${caseId}?tab=subjects`);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-300 p-6">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  const field = (label, key, opts = {}) => (
    <div>
      <Label className="text-slate-300">{label}</Label>
      {opts.textarea ? (
        <Textarea value={form[key] || ''} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          className="bg-slate-800 border-slate-600 text-white" rows={opts.rows || 3} placeholder={opts.placeholder} />
      ) : (
        <Input value={form[key] || ''} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          type={opts.type || 'text'} className="bg-slate-800 border-slate-600 text-white" placeholder={opts.placeholder}
          data-testid={opts.testid} />
      )}
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-4" data-testid="subject-form-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" className="border-slate-600 !text-slate-200 hover:bg-slate-800"
          onClick={() => navigate(`/cases/${caseId}?tab=subjects`)} data-testid="subject-form-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Case
        </Button>
        <h1 className="text-2xl font-bold text-white">{isEdit ? 'Edit Person' : 'Add Person'}</h1>
      </div>

      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field('Full Name *', 'full_name', { testid: 'subject-name-input', placeholder: 'John Doe' })}
            <div>
              <Label className="text-slate-300">Role in Case</Label>
              <Select value={form.role_in_case || 'SUBJECT'} onValueChange={(v) => setForm({ ...form, role_in_case: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-600 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 text-white">
                  {ROLE_OPTIONS.map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {field('Aliases (comma-sep)', 'aliases', { placeholder: 'Johnny, JD' })}
            {field('Date of Birth', 'date_of_birth', { type: 'date' })}
            {field('Gender', 'gender')}
            {field('Race / Ethnicity', 'race_ethnicity')}
            {field('Height', 'height')}
            {field('Weight', 'weight')}
            {field('Hair Color', 'hair_color')}
            {field('Eye Color', 'eye_color')}
            {field('Employer', 'employer')}
            {field('Last Known Address', 'last_known_address')}
            {field('Phone (comma-sep)', 'phone_numbers', { placeholder: '555-1234, 555-5678' })}
            {field('Emails (comma-sep)', 'email_addresses', { placeholder: 'a@x.com, b@x.com' })}
            {field('Social Media (comma-sep)', 'social_media')}
            <div>
              <Label className="text-slate-300">Surveillance Risk Level</Label>
              <Select value={form.surveillance_risk_level || 'MEDIUM'} onValueChange={(v) => setForm({ ...form, surveillance_risk_level: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-600 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 text-white">
                  {RISK_LEVELS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {field('Physical Description', 'physical_description', { textarea: true })}
          {field('Scars / Tattoos / Marks', 'scars_tattoos_marks', { textarea: true })}
          {field('Criminal History', 'criminal_history', { textarea: true })}
          {field('Caution Indicators', 'caution_indicators', { textarea: true, placeholder: 'Armed / dangerous / medical, etc.' })}
          {field('Notes', 'notes', { textarea: true })}

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-700">
            <Button variant="outline" onClick={() => navigate(`/cases/${caseId}?tab=subjects`)}
              className="border-slate-600 !text-slate-200">Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60"
              data-testid="save-subject-btn">
              {busy ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />{isEdit ? 'Saving…' : 'Adding…'}</>
                : <>{isEdit ? <><Save className="w-4 h-4 mr-2" />Save Changes</> : <><UserPlus className="w-4 h-4 mr-2" />Add Person</>}</>}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubjectFormPage;
